import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Link as RouterLink } from 'react-router-dom'
import {
  getActivityMatrix,
  getActivityMatrixOrgs,
  type ActivityMatrixResponse,
  type ActivityMatrixOrg,
} from '../utils/forecastApi'

function daysAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const d = Math.floor(
    (Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24)
  )
  return `${d}d ago`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

interface ClickableCellProps {
  value: number
  signup: string
  category: string
  onClick: (signup: string, category: string) => void
}

const ClickableCell: React.FC<ClickableCellProps> = ({
  value,
  signup,
  category,
  onClick,
}) => {
  if (!value) return <>-</>
  return (
    <Link
      component="button"
      variant="body2"
      sx={{ fontWeight: 'bold', cursor: 'pointer' }}
      onClick={() => onClick(signup, category)}
    >
      {value}
    </Link>
  )
}

const OrgActivityMatrix: React.FC = () => {
  const theme = useTheme()
  const [activityMatrix, setActivityMatrix] =
    useState<ActivityMatrixResponse | null>(null)
  const [paidSummary, setPaidSummary] = useState<ActivityMatrixResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [membership, setMembership] = useState('paid')

  // Drill-down state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogTitle, setDialogTitle] = useState('')
  const [dialogOrgs, setDialogOrgs] = useState<ActivityMatrixOrg[]>([])
  const [dialogLoading, setDialogLoading] = useState(false)

  // Always fetch paid summary for the top-level widget
  useEffect(() => {
    getActivityMatrix('paid')
      .then(setPaidSummary)
      .catch((err) => console.error('Error loading paid summary:', err))
  }, [])

  useEffect(() => {
    setLoading(true)
    getActivityMatrix(membership)
      .then(setActivityMatrix)
      .catch((err) => console.error('Error loading activity matrix:', err))
      .finally(() => setLoading(false))
  }, [membership])

  const handleCellClick = async (signup: string, category: string) => {
    const labels: Record<string, string> = {
      active: 'Active',
      dormant: 'Dormant',
      inactive: 'Inactive',
    }
    setDialogTitle(`${labels[category] || category} orgs — signed up ${signup}`)
    setDialogOpen(true)
    setDialogLoading(true)
    setDialogOrgs([])
    try {
      const orgs = await getActivityMatrixOrgs(signup, category, membership)
      setDialogOrgs(orgs)
    } catch (err) {
      console.error('Error loading orgs:', err)
    } finally {
      setDialogLoading(false)
    }
  }

  const pctColor = (pct: number) =>
    pct >= 60 ? 'error.main' : pct >= 30 ? 'warning.main' : 'success.main'

  // Compute paid totals for top summary — use live data when on paid tab, else separate fetch
  const paidTotals = useMemo(() => {
    const source = membership === 'paid' ? activityMatrix : paidSummary
    if (!source) return null
    let active = 0, dormant = 0, inactive = 0
    for (const signup of source.signup_buckets) {
      const row = source.matrix[signup]
      active += (row['<7 days ago'] || 0) + (row['7-30 days ago'] || 0)
      dormant += (row['30-90 days ago'] || 0) + (row['90-180 days ago'] || 0)
      inactive += (row['180+ days ago'] || 0) + (row['Never'] || 0)
    }
    const total = active + dormant + inactive
    return {
      active,
      dormant,
      inactive,
      total,
      pctActive: total ? Math.round((active / total) * 100) : 0,
      pctDormant: total ? Math.round((dormant / total) * 100) : 0,
      pctInactive: total ? Math.round((inactive / total) * 100) : 0,
    }
  }, [membership, activityMatrix, paidSummary])

  const { summaryRows, totals, totalPctInactive } = useMemo(() => {
    if (!activityMatrix) return { summaryRows: [], totals: { active: 0, dormant: 0, inactive: 0, total: 0 }, totalPctInactive: 0 }
    const rows = activityMatrix.signup_buckets.map((signup) => {
      const row = activityMatrix.matrix[signup]
      const active = (row['<7 days ago'] || 0) + (row['7-30 days ago'] || 0)
      const dormant = (row['30-90 days ago'] || 0) + (row['90-180 days ago'] || 0)
      const inactive = (row['180+ days ago'] || 0) + (row['Never'] || 0)
      const total = active + dormant + inactive
      const pctInactive = total > 0 ? Math.round((inactive / total) * 100) : 0
      return { signup, active, dormant, inactive, total, pctInactive }
    })
    const t = rows.reduce(
      (acc, r) => ({ active: acc.active + r.active, dormant: acc.dormant + r.dormant, inactive: acc.inactive + r.inactive, total: acc.total + r.total }),
      { active: 0, dormant: 0, inactive: 0, total: 0 }
    )
    return {
      summaryRows: rows,
      totals: t,
      totalPctInactive: t.total > 0 ? Math.round((t.inactive / t.total) * 100) : 0,
    }
  }, [activityMatrix])

  return (
    <>
      {/* Paid org health summary */}
      {paidTotals && (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 2,
            justifyContent: 'center',
          }}
        >
          {[
            { label: 'Active', count: paidTotals.active, pct: paidTotals.pctActive, color: theme.palette.success.main },
            { label: 'Dormant', count: paidTotals.dormant, pct: paidTotals.pctDormant, color: theme.palette.warning.main },
            { label: 'Inactive', count: paidTotals.inactive, pct: paidTotals.pctInactive, color: theme.palette.error.main },
          ].map((item) => (
            <Paper
              key={item.label}
              elevation={1}
              sx={{
                px: 3,
                py: 1.5,
                textAlign: 'center',
                minWidth: 140,
                borderTop: `4px solid ${item.color}`,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: item.color }}>
                {item.pct}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {item.label} ({item.count}/{paidTotals.total})
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      <Card elevation={2}>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Typography variant="h5">Organisation Activity</Typography>
            <ToggleButtonGroup
              value={membership}
              exclusive
              onChange={(_, val) => val && setMembership(val)}
              size="small"
            >
              <ToggleButton value="paid">Paid</ToggleButton>
              <ToggleButton value="free">Free</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Activity = last scan, funding need, or application. Active &lt;30d,
            Dormant 30-180d, Inactive 180d+ or never.
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : activityMatrix ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Sign Up Age</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Active</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Dormant</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Inactive</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>% Inactive</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summaryRows.map((r) => (
                    <TableRow key={r.signup} hover>
                      <TableCell>
                        <strong>{r.signup}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <ClickableCell
                          value={r.active}
                          signup={r.signup}
                          category="active"
                          onClick={handleCellClick}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <ClickableCell
                          value={r.dormant}
                          signup={r.signup}
                          category="dormant"
                          onClick={handleCellClick}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <ClickableCell
                          value={r.inactive}
                          signup={r.signup}
                          category="inactive"
                          onClick={handleCellClick}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <strong>{r.total}</strong>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            color: pctColor(r.pctInactive),
                          }}
                        >
                          {r.pctInactive}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow
                    sx={{ backgroundColor: theme.palette.grey[100] }}
                  >
                    <TableCell>
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>{totals.active}</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>{totals.dormant}</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>{totals.inactive}</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>{totals.total}</strong>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          color: pctColor(totalPctInactive),
                        }}
                      >
                        {totalPctInactive}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" color="textSecondary">
              No activity data available
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Drill-down dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{dialogTitle}</Typography>
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {dialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : dialogOrgs.length === 0 ? (
            <Typography color="textSecondary">No organisations found.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Organisation</strong></TableCell>
                    <TableCell align="center"><strong>Tier</strong></TableCell>
                    <TableCell align="center"><strong>Membership</strong></TableCell>
                    <TableCell align="center"><strong>Signed Up</strong></TableCell>
                    <TableCell align="center"><strong>Last Activity</strong></TableCell>
                    <TableCell align="center"><strong>Scans</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dialogOrgs.map((org) => (
                    <TableRow key={org.uuid} hover>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/orgs/${org.uuid}/details`}
                          onClick={() => setDialogOpen(false)}
                        >
                          {org.name}
                        </Link>
                      </TableCell>
                      <TableCell align="center">
                        {org.tier || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {org.membership || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {formatDate(org.created_at)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          sx={{
                            color: org.last_activity ? 'text.primary' : 'error.main',
                          }}
                        >
                          {daysAgo(org.last_activity)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {org.total_scans}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default OrgActivityMatrix
